import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { fetchLeaderboard, type LeaderboardRow, type Mode, type Period } from '../net/stats.js';

/**
 * Sıralama.
 *
 * Lider AYRI çiziliyor, listenin ilk satırı olarak değil: bu oyunda birinci
 * olmak "Başgan" olmak demek ve masadaki bütün dil onun üzerine kurulu.
 * Tabloda herkesi eşit satırlara dizmek o farkı siliyordu.
 *
 * Dönem ayrımı oyunlardaki gibi: bugün, bu hafta, tüm zamanlar. Haftalık
 * varsayılan — tüm zamanlar tablosu bir süre sonra donuyor ve sonradan
 * katılanın tutunacağı yer kalmıyor.
 */

const PERIODS: { key: Period; label: string }[] = [
  { key: 'day', label: 'Bugün' },
  { key: 'week', label: 'Bu hafta' },
  { key: 'all', label: 'Tüm zamanlar' },
];

export function Stats({ me, onClose }: { me: string | null; onClose(): void }) {
  const [period, setPeriod] = useState<Period>('week');
  const [mode, setMode] = useState<Mode>('online');
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);

  useEffect(() => {
    let disposed = false;
    setRows(null);
    void fetchLeaderboard(period, mode).then((data) => {
      if (!disposed) setRows(data);
    });
    return () => {
      disposed = true;
    };
  }, [period, mode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [leader, ...rest] = rows ?? [];

  return (
    <motion.div
      className="scrim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.section
        className="profile board-panel"
        initial={{ y: 16, scale: 0.97 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 8, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="profile__head board-panel__head">
          <span className="profile__who">
            <b>Sıralama</b>
            <em>{mode === 'online' ? 'gerçek oyuncularla' : 'botlara karşı'}</em>
          </span>

          <div className="profile__modes">
            <button
              type="button"
              className={mode === 'online' ? 'chip chip--on' : 'chip'}
              onClick={() => setMode('online')}
            >
              Online
            </button>
            <button
              type="button"
              className={mode === 'bot' ? 'chip chip--on' : 'chip'}
              onClick={() => setMode('bot')}
            >
              Botlar
            </button>
          </div>

          <button type="button" className="profile__close" onClick={onClose} aria-label="Kapat">
            ✕
          </button>
        </header>

        <div className="periods">
          {PERIODS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={period === item.key ? 'period period--on' : 'period'}
              onClick={() => setPeriod(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {rows === null ? (
          <p className="profile__empty">Yükleniyor…</p>
        ) : rows.length === 0 ? (
          <p className="profile__empty">
            {period === 'day'
              ? 'Bugün maç bitmedi.'
              : period === 'week'
                ? 'Bu hafta maç bitmedi.'
                : 'Henüz maç bitmedi.'}
          </p>
        ) : (
          <>
            {/* Lider tahtı: dönemin Başgan'ı. */}
            <div className={leader!.username === me ? 'throne throne--me' : 'throne'}>
              <span className="throne__crown">Başgan</span>
              <span className="throne__name">{leader!.displayName}</span>
              <span className="throne__figures">
                <b>{leader!.totalPoints}</b>
                <em>puan</em>
              </span>
              <span className="throne__detail">
                {leader!.matches} maç · {leader!.wins} birincilik · ort.{' '}
                {leader!.avgPlace === null ? '–' : leader!.avgPlace.toFixed(2)}
              </span>
            </div>

            {rest.length > 0 && (
              <ol className="ranks">
                {rest.map((row, index) => (
                  <li key={row.userId} className={row.username === me ? 'is-me' : undefined}>
                    <span className="ranks__no">{index + 2}</span>
                    <span className="ranks__name">{row.displayName}</span>
                    <span className="ranks__meta">
                      {row.matches} maç · {row.wins} birincilik
                    </span>
                    <b className="ranks__points">{row.totalPoints}</b>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </motion.section>
    </motion.div>
  );
}
